import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import type { CommonalityValueType } from '@/types/commonality';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';

export type GlobalCommonalityGridAttribute = Readonly<{
  frontend_name: string | null;
  value_type: string | null;
  channel_attribute_id: string | null;
  is_mappable: 0 | 1;
  allow_free_values: 0 | 1;
  example_attribute_id: number | null;
  documentation: string | null;
  max_min_occurs: number | null;
  total_assignments: number;
  posting_template_count: number;
}>;

export type GlobalCommonalityGridMapping = Readonly<{
  id: number;
  default_value_type: CommonalityValueType;
  default_value: string;
  grouping_id: number | null;
  enabled: 0 | 1;
  updated_at: string | null;
}>;

export type GlobalCommonalityGridRow = Readonly<{
  commonality_key: string;
  attribute: GlobalCommonalityGridAttribute | null;
  mapping: GlobalCommonalityGridMapping | null;
}>;

export type GlobalCommonalityGridData = Readonly<{
  global_set_id: number;
  channel_name: string;
  channel_version: string | null;
  rows: GlobalCommonalityGridRow[];
  total: number;
  filled: number;
}>;

type GlobalCommonalityGridResponse = Readonly<{
  data: GlobalCommonalityGridData;
}>;

export type GlobalCommonalityGridFilter = 'all' | 'filled' | 'empty';

export async function getGlobalCommonalityGrid(
  scope: RequestScope,
  args: {
    globalSetId: number;
    search?: string;
    only?: GlobalCommonalityGridFilter;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GlobalCommonalityGridData>> {
  try {
    if (!args.globalSetId) {
      throw new Error('globalSetId is required');
    }

    const params: Record<string, string | number> = {
      global_set_id: args.globalSetId,
    };
    if (args.search) params.search = args.search;
    if (args.only) params.only = args.only;
    if (typeof args.limit === 'number') params.limit = args.limit;
    if (typeof args.offset === 'number') params.offset = args.offset;

    const res = await scopedChannelApi(scope).get<GlobalCommonalityGridResponse>('atts/commonality/global/grid', {
      ...options,
      headers: buildAsturHeaders(),
      params,
      signal: args.signal ?? options.signal,
    });

    if (!res.data?.data) {
      throw new Error('Invalid global commonality grid response');
    }

    return {
      data: res.data.data,
      status: res.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
